import { camelCase } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import OneSchemaImporter from '@oneschema/react';
import {
  ContractorsOnboardCsvUploadContractDocumentType,
  CreateContractorsCsvRequest,
  InvoiceCycle,
  PaymentFrequency,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/contractor';
import { ContractType } from '@thera-hr/api-sdk/api/resources/model/resources/thera';

import LoadingScreen from 'components/container-components/LoadingScreen';
import SplitContentLayout from 'components/container-components/layout/SplitContentLayout';
import { WebOnlyAction } from 'components/app-components/WebOnlyAction';
import Button from 'components/app-components/ui/button';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useCompany } from 'context/company';
import { useUser } from 'context/user';
import BackCaretArrow from 'icons/BackCaretArrow';
import { UploadIcon } from 'icons/UploadIcon';
import { getOneSchemaJWT } from 'services/oneSchemaApi';
import { logError } from 'utils/error';
import LoadingSpinner from 'icons/LoadingSpinner';
import { AdminRoutes, CommonRoutes } from 'routes';
import { usePermissions } from 'hooks/usePermissions';
import { useTheraFernClient } from 'hooks/useFern';

const clientId = process.env.NEXT_PUBLIC_ONESCHEMA_CLIENT_ID;
const templateKey = process.env.NEXT_PUBLIC_ONESCHEMA_TEMPLATE_KEY_CONTRACTOR_UPLOAD;

export interface ICSVUploadFormData {
  request: CreateContractorsCsvRequest;
  errors: { email: string; errorMessage: string }[];
}

export default function Index() {
  const router = useRouter();
  const { isLoading, isLoggingOut } = useUser() || {};
  const { theraFernApi } = useTheraFernClient();
  const { company } = useCompany();
  const { isOrgAdmin, isHRSpecialist } = usePermissions();
  const isUserAllowed = isOrgAdmin || isHRSpecialist;

  const [isUploading, setIsUploading] = useState(false);
  const [isOneSchemaUIOpen, setIsOneSchemaUIOpen] = useState(false);
  const [oneSchemaJwtToken, setOneSchemaJwtToken] = useState<string>();
  const [csvRequest, setCsvRequest] = useState<ICSVUploadFormData>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!isUserAllowed) return;
    const getJWT = async () => {
      await getOneSchemaJWT(company?.companyID)
        .then((res) => {
          setOneSchemaJwtToken(res.jwtToken);
        })
        .catch((err) => {
          logError(err);
        });
    };
    getJWT();
  }, [company, isUserAllowed]);

  const handleContractorCreation = async (request) => {
    // Reset state
    setError(null);
    setCsvRequest(null);
    setIsUploading(true);
    await theraFernApi.thera.contractor.contractorApi
      .onboardContractorsCsv(
        company?.companyID,
        { ...request },
        {
          timeoutInSeconds: 180,
        },
      )
      .then((response: any) => {
        const errorArray = createErrorsArray(response?.errorsByEmployeeEmail);
        setCsvRequest({ request, errors: errorArray });
        if (!errorArray?.length) {
          theraToast.success('Contractors added');
        } else {
          theraToast.error({
            message: 'Failed to add some contractors',
          });
          setError('Failed to add some contractors');
        }
      })
      .catch((error) => {
        theraToast.error({
          message: 'Failed to add contractors',
          error: error,
          options: {
            id: 'failed-to-add-contractors',
          },
        });
        if (error?.body?.displayMessage) {
          setError(error?.body?.displayMessage);
        } else if (error?.body?.errorsByEmployeeEmail) {
          setError('Failed to add contractors');
          const errorArray = createErrorsArray(error.body.errors?.errorsByEmployeeEmail);
          setCsvRequest({ request, errors: errorArray });
        } else {
          setError('Failed to add contractors');
        }
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const normaliseOneSchemaResponse = async (data) => {
    const columns = data.columns.map((col) => {
      const formattedColumn = Object.entries(col).reduce((acc, field) => {
        const key = camelCase(field[0]);
        acc[key] = field[1];
        return acc;
      }, {});
      return formattedColumn;
    });
    const records = data.records.map((record) => {
      const formattedRecord = Object.entries(record).reduce((acc, field) => {
        const key = camelCase(field[0]);

        // handle type casting for fern
        if (
          key === 'contractRate' ||
          key === 'invoiceWorkEndsOnXDay' ||
          key === 'paymentDueXDaysAfterWorkCycle' ||
          key === 'firstPayTotal' ||
          key === 'firstPaymentDate' ||
          key === 'allowedWorkSubmitter'
        ) {
          if (!field[1]) return acc;
          if (key === 'firstPaymentDate' || key === 'allowedWorkSubmitter') {
            acc[key] = field[1];
          } else {
            acc[key] = Number(field[1]);
          }
        } else if (key === 'contractType') {
          acc[key] = field[1] as ContractType;
        } else if (key === 'paymentFrequency') {
          if (!field[1] || field[1] === '') return acc; // return if empty string as it is an optional value
          acc[key] = field[1] as PaymentFrequency;
        } else if (key === 'invoiceCycle') {
          if (!field[1]) return acc;
          acc[key] = field[1] as InvoiceCycle;
        } else if (key === 'ContractorsOnboardCsvUploadContractDocumentType') {
          acc[key] = field[1] as ContractorsOnboardCsvUploadContractDocumentType;
        } else {
          acc[key] = field[1];
        }

        if (key === '$custom') {
          acc['custom'] = field[1];
        }
        return acc;
      }, {});
      return formattedRecord;
    });
    const invalidRows = records.filter(
      (r: any) => r.invoiceCycle === 'SEMIMONTHLY' && (r.invoiceWorkEndsOnXDay < 1 || r.invoiceWorkEndsOnXDay > 15),
    );
    if (invalidRows.length > 0) {
      theraToast.error({
        message: `Invoice work end day must be 1–14 (custom day) or 15 (15th & end of month) for semimonthly contracts (${invalidRows.length} row${invalidRows.length !== 1 ? 's' : ''} affected)`,
        options: { id: 'invalid-invoice-work-ends-on-x-day' },
      });
      return;
    }

    const formattedCsvRequest = { columns, records };
    await handleContractorCreation(formattedCsvRequest);
  };

  useEffect(() => {
    // stop body from scrolling when OpenSchema iframe is open
    if (!isOneSchemaUIOpen) return;
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isOneSchemaUIOpen]);

  if (isLoading || isLoggingOut) {
    return <LoadingScreen />;
  }

  if (!isUserAllowed) {
    router.push(CommonRoutes.NotFound);
  }

  return (
    <>
      <SplitContentLayout imageSrc="/images/banner/booking-page.svg" imageAlt="Thera Booking">
        <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-lg">
          <button
            type="button"
            onClick={() => router.push(AdminRoutes.AddContractor)}
            className="mb-3 inline-flex items-center gap-2 text-base font-medium text-therablue"
          >
            <BackCaretArrow stroke="currentColor" scale={2} />
            Back
          </button>
          <h1 className="text-2xl font-bold text-neutral-900">Bulk upload contractors</h1>

          <div className="mt-2">
            {isUploading ? (
              <LoadingSpinner />
            ) : (
              <>
                <p className="text-sm text-neutral-600">
                  <a
                    href="https://docs.google.com/spreadsheets/d/1IZeTHxbo2sQK1wISYO8xTZaHsRY-z2a83r_vuXnxhKg/export?format=xlsx"
                    className="font-medium text-therablue underline"
                  >
                    Download our template
                  </a>{' '}
                  and add one contractor per row. Then upload the file here.
                </p>
                <div className="mt-3">
                  <WebOnlyAction message="Contractor bulk upload is available on the web app. Open app.getthera.com on your computer.">
                    <Button variant="primary" onClick={() => setIsOneSchemaUIOpen(true)} loading={isOneSchemaUIOpen}>
                      <UploadIcon className="h-4 w-4" strokeColor="white" />
                      <span className="ml-2 text-sm font-medium">Choose CSV file</span>
                    </Button>
                  </WebOnlyAction>
                </div>
                <p className="mt-2 text-xs text-neutral-500">CSV files only · Maximum 10 MB</p>
                {(error || csvRequest?.errors?.length > 0) && (
                  <a href={csvRequest ? getCSVURL(csvRequest?.errors) : '#'} download={'contractor_errors.csv'}>
                    <Button variant="plain" className="mt-2 text-sm font-medium text-neutral-600 underline">
                      Download Error CSV
                    </Button>
                  </a>
                )}
              </>
            )}
          </div>

          <div className="mt-5 space-y-2 border-t border-neutral-200 pt-5">
            <h2 className="text-base font-semibold text-neutral-900">Before you upload</h2>
            {[
              {
                title: 'Use our template',
                description: 'Download the template so your columns match exactly what we expect.',
              },
              {
                title: 'One contractor per row',
                description: 'Each row should contain one contractor’s full contract details.',
              },
              {
                title: 'Review your file against the template',
                description: 'If any rows have issues after you upload, we’ll flag them so you can fix and re-upload.',
              },
            ].map((step, index) => (
              <div key={step.title} className={`flex gap-3 pt-2 ${index > 0 ? 'border-t border-neutral-200' : ''}`}>
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-sm font-medium text-neutral-700">
                  {index + 1}
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-900">{step.title}</p>
                  <p className="text-xs text-neutral-500">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SplitContentLayout>
      {oneSchemaJwtToken && isOneSchemaUIOpen && (
        <OneSchemaImporter
          /* managing state from your application */
          isOpen={isOneSchemaUIOpen}
          onRequestClose={() => setIsOneSchemaUIOpen(false)}
          /* required config values */
          clientId={clientId}
          userJwt={oneSchemaJwtToken}
          templateKey={templateKey}
          /* optional config values */
          importConfig={{ type: 'local', metadataOnly: false }}
          devMode={process.env.NODE_ENV !== 'production'}
          className="oneschema-importer"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 2147483002, // make sure this value is always higher than intercom's z-index
          }}
          /* handling results */
          onSuccess={normaliseOneSchemaResponse}
          onCancel={() => {
            csvRequest && setCsvRequest(null);
            console.warn('oneschema import cancelled');
          }}
          onError={(message) => console.error(message)}
        />
      )}
    </>
  );
}

const getCSVURL = (rawErrorData: ICSVUploadFormData['errors']) => {
  let csv = 'Email,Error Message\r\n';
  // create a CSV
  rawErrorData.forEach(function (row) {
    csv += `${row.email},${row.errorMessage}\r\n`;
  });

  const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const csvURL = window.URL.createObjectURL(csvData);
  return csvURL;
};

const createErrorsArray = (errors) => {
  if (!errors || !Object.keys(errors)?.length) return;

  const errorArray = Object.entries(errors).map(([email, error]: [string, any]) => {
    return { ['email']: email, ['errorMessage']: error?.problemDetail?.detail || 'Something went wrong!' };
  });
  return errorArray;
};
